import { getLangFromTaxIdentifier, isFrenchLocale } from '@repo/utils'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { v4 as uuidv4 } from 'uuid'

import s from './styles.module.scss'

import {
  getDetailsBillingSectionProps,
  getProposalDetailsSummaryColumns,
  getTotalPrice,
  ProposalDetailsTotalColumns,
} from './utils/utils'
import { DescriptionMode } from '../details-container/DescriptionMode'
import DetailsBillingSection from '../details-container/detail-billing-sections/details-billing-section/DetailsBillingSection'
import DetailsTotal from '../details-container/details-total/DetailsTotal'
import DetailsTotalOwner from '../details-container/details-total-owner/DetailsTotalOwner'
import DetailsVat from '../details-container/details-vat/DetailsVat'
import DetailsContainer from '../details-container/DetailsContainer'
import {
  ClientCarbonFootprintInputDto,
  ClientPricingItemTypeEnum,
  ComputedPrice,
  DetailsBillingSectionClientPricingItemFragment,
  ProposalDetailsSummaryCarbonEmissionsFragment,
  ProposalDetailsSummaryClientPricingItemFragment,
  ProposalDetailsSummaryPriceFragment,
  TaxIdentifierEnum,
  TravelTypeEnum,
} from '../../../../../lib/graphql'
import { isNumber } from '../../../../../utils/format-numbers'
import { HorizontalSeparator, Icon } from '../../../../atoms'
import {
  UserTypeContextEnum,
  useTravelTypeContext,
  useUserTypeContext,
} from '../../../../contexts'
import CarbonFootprintDetails from '../../../carbon-footprint/CarbonFootprintDetails'
import CarbonFootprintDialog from '../../../carbon-footprint/CarbonFootprintDialog'

export interface IProposalDetailsSummaryProps {
  //we cannot use price.priceItems to display because of adjustment tab : price is used for the total, clientPricingItems for the details
  price: ProposalDetailsSummaryPriceFragment
  clientPricingItems: ProposalDetailsSummaryClientPricingItemFragment[]
  adults: number
  className?: string
  depositAmount?: ComputedPrice
  balanceAmount?: ComputedPrice
  balancePostFinalAmount?: ComputedPrice
  showAdjustments?: boolean
  isModal?: boolean
  carbonFootprintInfos?: ProposalDetailsSummaryCarbonEmissionsFragment | null
  onValidateCarbonFootprintInfos?: (
    carbonFootprint: ClientCarbonFootprintInputDto,
  ) => void
  taxIdentifier?: TaxIdentifierEnum
  hideTotalPrice?: boolean
  isCarbonFootprintLoading?: boolean
  houseLocation?: google.maps.LatLngLiteral
}

const ProposalDetailsSummary = ({
  price,
  adults,
  depositAmount,
  balanceAmount,
  balancePostFinalAmount,
  showAdjustments = false,
  clientPricingItems,
  isModal = false,
  carbonFootprintInfos,
  onValidateCarbonFootprintInfos,
  isCarbonFootprintLoading,
  taxIdentifier,
  hideTotalPrice,
  houseLocation,
}: IProposalDetailsSummaryProps) => {
  const { travelType } = useTravelTypeContext()
  const intl = useIntl()
  const { userType } = useUserTypeContext()
  const [isCarbonFootprintModalOpen, setIsCarbonFootprintModalOpen] =
    useState<boolean>(false)
  const taxLang = getLangFromTaxIdentifier(taxIdentifier)

  const openCarbonFootprintModal = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsCarbonFootprintModalOpen(true)
  }

  const totalPrice = getTotalPrice({
    price: price.totalUser ?? undefined,
    userType,
  })

  const columns = getProposalDetailsSummaryColumns({ userType })

  const detailsBillingSectionProps = getDetailsBillingSectionProps({
    clientPricingItems,
    intl,
  })

  const isOwner = userType === UserTypeContextEnum.OWNER
  const isCarbonFootprintAlreadyComputed = isNumber(
    carbonFootprintInfos?.carbonEmissions?.totalEmissionsCount,
  )
  const feesAdjustmentsItems = clientPricingItems.filter(
    (clientPricingItem) =>
      clientPricingItem.type === ClientPricingItemTypeEnum.FEES_ADJUSTMENTS,
  )
  const feesAdjustmentsDisplayedItems: DetailsBillingSectionClientPricingItemFragment[] =
    feesAdjustmentsItems.map((clientPricingItem) => ({
      ...clientPricingItem,
      unitPrice: {
        basePrice:
          clientPricingItem.totalPrice?.[
            isOwner ? 'priceWithOwnerFees' : 'priceWithUserFees'
          ],
      },
      totalPrice: {
        basePrice:
          clientPricingItem.totalPrice?.[
            isOwner ? 'priceWithOwnerFees' : 'priceWithUserFees'
          ],
      },
      priceOption: {
        ...clientPricingItem.priceOption,
        quantity: clientPricingItem.priceOption?.quantity ?? 0,
        fees: {
          ...clientPricingItem.priceOption?.fees,
          ownerFeesRate: undefined,
        },
      },
      priceUnit: undefined,
    }))

  return (
    <div className={s.detailsSummary}>
      {!isOwner && onValidateCarbonFootprintInfos && (
        <CarbonFootprintDialog
          hideTrigger={isCarbonFootprintAlreadyComputed}
          isOpen={isCarbonFootprintModalOpen}
          onOpenChange={(open) => setIsCarbonFootprintModalOpen(open)}
          carbonFootprintData={{
            totalEmission:
              carbonFootprintInfos?.carbonEmissions?.totalEmissionsCount ?? 0,
            housingEmission:
              carbonFootprintInfos?.carbonEmissions
                ?.accommodationEmissionsCount ?? 0,
            restorationEmission:
              carbonFootprintInfos?.carbonEmissions?.cateringEmissionsCount ??
              0,
            transportEmission:
              carbonFootprintInfos?.carbonEmissions?.transportEmissionsCount ??
              0,
            activitiesEmission:
              carbonFootprintInfos?.carbonEmissions?.activitiesEmissionsCount ??
              0,
          }}
          computeCarbonFootprint={{
            isLoading: isCarbonFootprintLoading ?? false,
            onSubmit: (data) => {
              // TODO - Update correctly departureCityPlaceId & transportDistanceInMetersCount
              onValidateCarbonFootprintInfos({
                departureCity: {
                  description: data.departureCity.description,
                  placeId: data.departureCity.placeId,
                  distanceInMeters: data.departureCity.distance_meters,
                },
                lastKmsTransport: data.endOfJourney,
                mealsType: data.mealType,
                transport: data.transportMode,
              })
            },
            defaultValues: {
              departureCity: carbonFootprintInfos?.departureCity
                ? {
                    description:
                      carbonFootprintInfos?.departureCity.description,
                    placeId: carbonFootprintInfos?.departureCity.placeId,
                    distance_meters:
                      carbonFootprintInfos?.departureCity.distanceInMeters,
                  }
                : undefined,
              transportMode: carbonFootprintInfos?.transport ?? undefined,
              endOfJourney: carbonFootprintInfos?.lastKmsTransport ?? undefined,
              mealType: carbonFootprintInfos?.mealsType ?? undefined,
            },
          }}
          houseLocation={houseLocation}
        />
      )}
      {detailsBillingSectionProps.map((section, index) => (
        <React.Fragment key={section.categories.join()}>
          {index > 0 && <div className={s.separator} />}
          <DetailsBillingSection
            clientPricingItems={section.clientPricingItems}
            title={{
              label: section.label,
              icon: (
                <Icon
                  size={13}
                  name={section.icon}
                  style={{ marginRight: 7 }}
                  // color is functionnaly wrong but that's what's wanted
                  color='var(--clr-action-secondary-text)'
                />
              ),
            }}
            columns={columns}
            descriptionMode={DescriptionMode.toggable}
            showAdjustments={showAdjustments}
            isModal={isModal}
          />
        </React.Fragment>
      ))}

      {isOwner && (
        <DetailsBillingSection
          clientPricingItems={feesAdjustmentsDisplayedItems}
          title={{
            label: 'Naboo',
            icon: (
              <Icon
                size={13}
                name='info-filled'
                style={{ marginRight: 7 }}
                // color is functionnaly wrong but that's what's wanted
                color='var(--clr-action-secondary-text)'
              />
            ),
          }}
          columns={columns}
          descriptionMode={DescriptionMode.toggable}
          showAdjustments={showAdjustments}
          isModal={isModal}
        />
      )}

      {userType === UserTypeContextEnum.USER ? (
        <>
          {detailsBillingSectionProps.length > 0 && (
            <div className={s.separator} />
          )}
          <DetailsBillingSection
            clientPricingItems={[
              {
                _id: uuidv4(),
                unitPrice: {
                  basePrice:
                    price.totalUserWithoutFeesAdjustments?.fees?.userFeesPrice,
                  priceWithDiscount:
                    price.totalUserWithoutFeesAdjustments?.feesWithDiscount
                      ?.userFeesPrice,
                },
                totalPrice: {
                  basePrice:
                    price.totalUserWithoutFeesAdjustments?.fees?.userFeesPrice,
                  priceWithDiscount:
                    price.totalUserWithoutFeesAdjustments?.feesWithDiscount
                      ?.userFeesPrice,
                },
                objectData: {
                  /**
                   * TODO: This should be handled by the backend
                   * @see https://github.com/naboo-team/naboo/pull/2324#discussion_r1808529626
                   */
                  label: !isFrenchLocale(taxLang)
                    ? 'Service fees'
                    : 'Frais de service',
                },
                priceOption: {
                  quantity: 1,
                  fees: undefined,
                },
              },
              ...feesAdjustmentsDisplayedItems,
            ]}
            columns={columns}
            descriptionMode={DescriptionMode.toggable}
            isModal={isModal}
            displayHeader={false}
          />
          {!hideTotalPrice && (
            <DetailsTotal
              label={
                travelType === TravelTypeEnum.CORPORATE_EVENING_EVENT
                  ? intl.formatMessage({
                      id: 'ui.ProposalDetailsSummary.DetailsTotal.event',
                      defaultMessage: 'événement',
                    })
                  : intl.formatMessage({
                      id: 'ui.ProposalDetailsSummary.DetailsTotal.sojourn',
                      defaultMessage: 'séjour',
                    })
              }
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              total={totalPrice}
              adults={adults}
              className={s.detailsTotal}
              depositAmount={depositAmount}
              balanceAmount={balanceAmount}
              balancePostFinalAmount={balancePostFinalAmount}
              columns={ProposalDetailsTotalColumns}
              descriptionMode={DescriptionMode.toggable}
              showAdjustments={showAdjustments}
            />
          )}
        </>
      ) : (
        <>
          {detailsBillingSectionProps.length > 0 && (
            <div className={s.separator} />
          )}
          <DetailsContainer
            title={intl.formatMessage({
              id: 'ui.ProposalDetailsSummary.DetailsTotal.totalTitle',
              defaultMessage: 'Total',
            })}
            columns={ProposalDetailsTotalColumns}
            descriptionMode={DescriptionMode.hidden}
            className={s.totalContainer}
            isModal={isModal}
          >
            <DetailsTotalOwner
              total={{
                price: price.totalUser?.priceWithDiscount as ComputedPrice,
                ownerFees: price.totalUser?.feesWithDiscount
                  ?.ownerFeesPrice as ComputedPrice,
                priceOwner: price.totalUser
                  ?.priceWithDiscountAndOwnerFees as ComputedPrice,
              }}
              className={s.detailsTotal}
              columns={ProposalDetailsTotalColumns}
              descriptionMode={DescriptionMode.toggable}
              showAdjustments={showAdjustments}
            />
          </DetailsContainer>
          <div className={s.separator} />
          <DetailsContainer
            title={intl.formatMessage({
              id: 'ui.ProposalDetailsSummary.DetailsTotal.vatTitle',
              defaultMessage: 'TVA',
            })}
            columns={ProposalDetailsTotalColumns}
            descriptionMode={DescriptionMode.hidden}
            className={s.totalContainer}
            isModal={isModal}
          >
            <DetailsVat
              total={{
                totalPerVatRates: price.totalPerVatRates ?? [],
                price: price.totalUser?.priceWithDiscount as ComputedPrice,
              }}
              className={s.detailsTotal}
              columns={ProposalDetailsTotalColumns}
              descriptionMode={DescriptionMode.toggable}
              showAdjustments={showAdjustments}
            />
          </DetailsContainer>
        </>
      )}

      {!isOwner && isCarbonFootprintAlreadyComputed && (
        <>
          <HorizontalSeparator />
          <CarbonFootprintDetails
            onModify={openCarbonFootprintModal}
            {...{
              totalEmission:
                carbonFootprintInfos?.carbonEmissions?.totalEmissionsCount ?? 0,
              housingEmission:
                carbonFootprintInfos?.carbonEmissions
                  ?.accommodationEmissionsCount ?? 0,
              restorationEmission:
                carbonFootprintInfos?.carbonEmissions?.cateringEmissionsCount ??
                0,
              transportEmission:
                carbonFootprintInfos?.carbonEmissions
                  ?.transportEmissionsCount ?? 0,
              activitiesEmission:
                carbonFootprintInfos?.carbonEmissions
                  ?.activitiesEmissionsCount ?? 0,
            }}
          />
        </>
      )}
    </div>
  )
}

export default ProposalDetailsSummary
